import { DashboardCard } from "@/components/ui/DashboardCard/DashboardCard";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/Table/Table";
import {
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
  type ColumnDef,
  type ColumnFiltersState,
  type PaginationState,
  type SortingState,
} from "@tanstack/react-table";
import { useEffect, useMemo, useState } from "react";
import { Badge } from "@/components/ui/Badge/Badge";
import { HStack } from "@/components/layout/HStack";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "@/components/ui/DropdownMenu/DropdownMenu";
import { Button } from "@/components/ui/Button/Button";
import {
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  Filter,
  UserCog,
} from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/Select/Select";
import { RESULTS_PER_PAGE_OPTIONS } from "@/constants/tableOptions";
import {
  Pagination,
  PaginationButton,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/Pagination/Pagination";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/Tooltip/Tooltip";
import { useSearchParams } from "react-router-dom";
import { parseCsv, writeCsv } from "@/utils/csv";
import { Input } from "@/components/ui/Input/Input";
import type { ApiUser } from "@shared/types/models";
import type { UserRole } from "@shared/constants";
import { USER_ROLES } from "@shared/constants";

const roleLabel: Record<UserRole, string> = {
  "sys-admin": "Sys-Admin",
  administrator: "Administrator",
  teacher: "Teacher",
  student: "Student",
};

const getColumns = ({
  onEdit,
}: {
  onEdit: (userId: string) => void;
}): ColumnDef<ApiUser>[] => [
  {
    accessorKey: "name",
    header: ({ column }) => (
      <Button
        variant="ghost"
        style={{
          fontWeight: "var(--font-medium)",
          height: "fit-content",
          padding: "var(--space-xxs) var(--space-xs)",
          marginLeft: "calc(var(--space-xs) * -1)",
        }}
        onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
      >
        Name{" "}
        {column.getIsSorted() === "asc" ? (
          <ArrowDown />
        ) : column.getIsSorted() === "desc" ? (
          <ArrowUp />
        ) : (
          <ArrowUpDown />
        )}
      </Button>
    ),
    cell: ({ row }) => row.original.name,
    filterFn: (row, _columnId, value: string) => {
      if (!value) return true;
      const v = value.toLowerCase();
      return (
        row.original.name.toLowerCase().includes(v) ||
        row.original.email.toLowerCase().includes(v)
      );
    },
  },
  {
    accessorKey: "email",
    header: "Email",
    cell: ({ row }) => row.original.email,
  },
  {
    accessorKey: "role",
    header: "Role",
    cell: ({ row }) => (
      <Badge variant="outline">{roleLabel[row.original.role]}</Badge>
    ),
    filterFn: (row, columnId, value: UserRole[] | undefined) => {
      if (!value || value.length === 0) return true;
      return value.includes(row.getValue(columnId) as UserRole);
    },
  },
  {
    accessorKey: "gradYear",
    header: ({ column }) => (
      <Button
        variant="ghost"
        style={{
          fontWeight: "var(--font-medium)",
          height: "fit-content",
          padding: "var(--space-xxs) var(--space-xs)",
          marginLeft: "calc(var(--space-xs) * -1)",
        }}
        onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
      >
        Grad Year{" "}
        {column.getIsSorted() === "asc" ? (
          <ArrowDown />
        ) : column.getIsSorted() === "desc" ? (
          <ArrowUp />
        ) : (
          <ArrowUpDown />
        )}
      </Button>
    ),
    cell: ({ row }) => row.original.gradYear ?? "—",
    sortingFn: (a, b) => {
      const av = a.original.gradYear ?? Number.POSITIVE_INFINITY;
      const bv = b.original.gradYear ?? Number.POSITIVE_INFINITY;
      return av - bv;
    },
  },
  {
    id: "actions",
    header: () => (
      <div style={{ textAlign: "right", fontWeight: "var(--font-medium)" }}>
        Actions
      </div>
    ),
    cell: ({ row }) => (
      <HStack justify="flex-end" gap="0.25rem">
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              size="icon-small"
              variant="outline"
              onClick={() => onEdit(row.original._id)}
            >
              <UserCog />
            </Button>
          </TooltipTrigger>
          <TooltipContent>Edit user</TooltipContent>
        </Tooltip>
      </HStack>
    ),
  },
];

const DataTable = ({
  data,
  columns,
}: {
  data: ApiUser[];
  columns: ColumnDef<ApiUser>[];
}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  });

  const sortKRaw = searchParams.get("sortK");
  const sortVRaw = searchParams.get("sortV");

  const urlSorting = useMemo<SortingState>(() => {
    const validK = sortKRaw === "name" || sortKRaw === "gradYear";
    const validV = sortVRaw === "asc" || sortVRaw === "desc";
    if (!validK || !validV) return [];
    return [{ id: sortKRaw, desc: sortVRaw === "desc" }];
  }, [sortKRaw, sortVRaw]);

  const onSortingChange = (
    updater: SortingState | ((old: SortingState) => SortingState),
  ) => {
    const next =
      typeof updater === "function" ? updater(urlSorting) : updater;

    setSearchParams((prev) => {
      const sp = new URLSearchParams(prev);
      const first = next[0];
      if (!first) {
        sp.delete("sortK");
        sp.delete("sortV");
        return sp;
      }
      sp.set("sortK", first.id);
      sp.set("sortV", first.desc ? "desc" : "asc");
      return sp;
    });
  };

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    onSortingChange,
    getSortedRowModel: getSortedRowModel(),
    onColumnFiltersChange: setColumnFilters,
    getFilteredRowModel: getFilteredRowModel(),
    onPaginationChange: setPagination,
    getPaginationRowModel: getPaginationRowModel(),
    state: {
      sorting: urlSorting,
      columnFilters,
      pagination,
    },
  });

  const urlRoles = useMemo(
    () => parseCsv(searchParams.get("role")) as UserRole[],
    [searchParams],
  );

  const availableRoles = useMemo(() => {
    const set = new Set<UserRole>();
    for (const u of data) set.add(u.role);
    return USER_ROLES.filter((r) => set.has(r));
  }, [data]);

  const toggleRole = (r: UserRole) => {
    const next = urlRoles.includes(r)
      ? urlRoles.filter((x) => x !== r)
      : [...urlRoles, r];
    setSearchParams((prev) => {
      const sp = new URLSearchParams(prev);
      writeCsv(sp, "role", next);
      return sp;
    });
  };

  const clearRoleFilter = () => {
    setSearchParams((prev) => {
      const sp = new URLSearchParams(prev);
      sp.delete("role");
      return sp;
    });
  };

  const search = searchParams.get("q") ?? "";
  const setSearch = (v: string) => {
    setSearchParams((prev) => {
      const sp = new URLSearchParams(prev);
      if (v) sp.set("q", v);
      else sp.delete("q");
      return sp;
    });
  };

  useEffect(() => {
    table.getColumn("role")?.setFilterValue(urlRoles);
    table.getColumn("name")?.setFilterValue(search);
    table.setPageIndex(0);
  }, [table, urlRoles, search]);

  const totalCount = table.getCoreRowModel().rows.length;
  const filteredCount = table.getFilteredRowModel().rows.length;

  const showPagination = table.getPageCount() > 1;

  function getPageItems(current: number, total: number) {
    const c = current + 1;
    const t = total;
    if (t <= 7) return Array.from({ length: t }, (_, i) => i + 1);
    const items: (number | "…")[] = [1];
    if (c > 3) items.push("…");
    const start = Math.max(2, c - 1);
    const end = Math.min(t - 1, c + 1);
    for (let p = start; p <= end; p++) items.push(p);
    if (c < t - 2) items.push("…");
    items.push(t);
    return items;
  }

  return (
    <>
      <HStack align="center" justify="space-between" wrap>
        <HStack align="center">
          <Input
            placeholder="Search name or email"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ width: "240px" }}
          />
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                style={{
                  height: "fit-content",
                  padding: "var(--space-xxs) var(--space-xs)",
                }}
              >
                <Filter /> Filter
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start">
              <DropdownMenuSub>
                <DropdownMenuSubTrigger>Role</DropdownMenuSubTrigger>
                <DropdownMenuSubContent>
                  {availableRoles.map((r) => (
                    <DropdownMenuCheckboxItem
                      key={r}
                      checked={urlRoles.includes(r)}
                      onCheckedChange={() => toggleRole(r)}
                      onSelect={(e) => e.preventDefault()}
                    >
                      {roleLabel[r]}
                    </DropdownMenuCheckboxItem>
                  ))}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onSelect={(e) => {
                      e.preventDefault();
                      clearRoleFilter();
                    }}
                    disabled={urlRoles.length === 0}
                  >
                    Clear
                  </DropdownMenuItem>
                </DropdownMenuSubContent>
              </DropdownMenuSub>
            </DropdownMenuContent>
          </DropdownMenu>
          <p style={{ color: "var(--muted-foreground)" }}>
            {filteredCount} of {totalCount} users
          </p>
        </HStack>
        <Select
          value={String(table.getState().pagination.pageSize)}
          onValueChange={(v) => {
            table.setPageSize(Number(v));
            table.setPageIndex(0);
          }}
        >
          <SelectTrigger
            style={{
              width: "fit-content",
              height: "fit-content",
              padding: "var(--space-xxs) var(--space-xs)",
            }}
          >
            <SelectValue placeholder="Select results per page" />
          </SelectTrigger>
          <SelectContent>
            {RESULTS_PER_PAGE_OPTIONS.map((option) => (
              <SelectItem key={option} value={String(option)}>
                {option} results/page
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </HStack>
      <Table borderColor="var(--secondary-border)">
        <TableHeader>
          {table.getHeaderGroups().map((h) => (
            <TableRow key={h.id}>
              {h.headers.map((header) => (
                <TableHead key={header.id}>
                  {header.isPlaceholder
                    ? null
                    : flexRender(
                        header.column.columnDef.header,
                        header.getContext(),
                      )}
                </TableHead>
              ))}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          {table.getRowModel().rows.length ? (
            table.getRowModel().rows.map((row) => (
              <TableRow key={row.id}>
                {row.getVisibleCells().map((cell) => (
                  <TableCell key={cell.id}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </TableCell>
                ))}
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={columns.length}>No records</TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
      {showPagination && (
        <Pagination>
          <PaginationContent>
            <PaginationItem>
              <PaginationPrevious
                onClick={(e) => {
                  e.preventDefault();
                  table.previousPage();
                }}
                disabled={!table.getCanPreviousPage()}
              />
            </PaginationItem>
            {getPageItems(pagination.pageIndex, table.getPageCount()).map(
              (it, idx) =>
                it === "…" ? (
                  <PaginationItem key={`el-${idx}`}>
                    <PaginationEllipsis />
                  </PaginationItem>
                ) : (
                  <PaginationItem key={it}>
                    <PaginationButton
                      isActive={it === pagination.pageIndex + 1}
                      onClick={(e) => {
                        e.preventDefault();
                        table.setPageIndex(it - 1);
                      }}
                    >
                      {it}
                    </PaginationButton>
                  </PaginationItem>
                ),
            )}
            <PaginationItem>
              <PaginationNext
                onClick={(e) => {
                  e.preventDefault();
                  table.nextPage();
                }}
                disabled={!table.getCanNextPage()}
              />
            </PaginationItem>
          </PaginationContent>
        </Pagination>
      )}
    </>
  );
};

const UsersTable = ({
  data,
  onEdit,
}: {
  data: ApiUser[];
  onEdit: (userId: string) => void;
}) => {
  const columns = useMemo(() => getColumns({ onEdit }), [onEdit]);

  return (
    <DashboardCard>
      <DataTable data={data} columns={columns} />
    </DashboardCard>
  );
};

export { UsersTable };
